import React, { Component } from "react";
import { Drawer, Table, Button, Tag, Tooltip, notification, Space } from "antd";
import {
  DownloadOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  fetchExportJobsAPI,
  downloadExportJobAPI,
} from "../../Graphs/Report/exportJobsAPI";

class ExportJobsDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      jobs: [],
      isLoading: false,
      downloadingJobId: null,
    };
    this.pollTimer = null;
  }

  componentDidMount() {
    if (this.props.visible) {
      this.loadJobs();
      this.startPolling();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.visible && this.props.visible) {
      this.loadJobs();
      this.startPolling();
    } else if (prevProps.visible && !this.props.visible) {
      this.stopPolling();
    }
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  startPolling = () => {
    this.stopPolling();
    this.pollTimer = setInterval(() => {
      const { jobs } = this.state;
      const hasActiveJobs = jobs.some(
        (j) => j.status === "pending" || j.status === "processing"
      );
      if (hasActiveJobs || jobs.length === 0) {
        this.loadJobs(false);
      }
    }, 5000);
  };

  stopPolling = () => {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  };

  loadJobs = async (showLoading = true) => {
    try {
      if (showLoading) {
        this.setState({ isLoading: true });
      }
      const { authToken } = this.props;
      if (!authToken) return;

      const res = await fetchExportJobsAPI(authToken);
      if (res && res.success && Array.isArray(res.data)) {
        this.setState({ jobs: res.data });
      }
    } catch (err) {
      console.error("Failed to load export jobs:", err);
    } finally {
      if (showLoading) {
        this.setState({ isLoading: false });
      }
    }
  };

  handleDownload = async (jobId) => {
    try {
      this.setState({ downloadingJobId: jobId });
      const { authToken } = this.props;
      await downloadExportJobAPI(authToken, jobId);
      notification.success({
        message: "Download Started",
        description: "Your report CSV file has been downloaded successfully.",
      });
    } catch (err) {
      notification.error({
        message: "Download Failed",
        description: err.message || "Failed to download export file.",
      });
    } finally {
      this.setState({ downloadingJobId: null });
    }
  };

  renderStatusTag = (status, progress) => {
    switch (status) {
      case "pending":
        return (
          <Tag icon={<ClockCircleOutlined />} color="default">
            Pending
          </Tag>
        );
      case "processing":
        return (
          <Tag icon={<SyncOutlined spin />} color="processing">
            Processing {progress ? `${progress}%` : ""}
          </Tag>
        );
      case "completed":
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Completed
          </Tag>
        );
      case "failed":
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Failed
          </Tag>
        );
      case "expired":
        return (
          <Tag icon={<ExclamationCircleOutlined />} color="warning">
            Expired
          </Tag>
        );
      default:
        return <Tag>{status}</Tag>;
    }
  };

  render() {
    const { visible, onClose } = this.props;
    const { jobs, isLoading, downloadingJobId } = this.state;

    const columns = [
      {
        title: "Target / Filter",
        key: "target",
        render: (_, record) => {
          const params = record.filter_params || {};
          const isAllTime = !params.start_date || !params.end_date;
          return (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 600 }}>{record.user_email}</span>
                {record.report_type === "user_recharge_collection_report" ? (
                  <Tag color="cyan">Recharges & Collections</Tag>
                ) : (
                  <Tag color="blue">Transactions</Tag>
                )}
              </div>
              <div style={{ fontSize: "12px", color: "#888", marginTop: 2 }}>
                {isAllTime ? (
                  <Tag color="purple">All Time History</Tag>
                ) : (
                  <span>
                    {params.start_date} ~ {params.end_date}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: "Status",
        key: "status",
        width: 140,
        render: (_, record) =>
          this.renderStatusTag(record.status, record.progress_percent),
      },
      {
        title: "Rows / Size",
        key: "size",
        width: 130,
        render: (_, record) => {
          if (record.status !== "completed") return "-";
          const rows = record.total_rows || 0;
          const sizeMb = record.file_size_bytes
            ? (record.file_size_bytes / (1024 * 1024)).toFixed(2)
            : "0.00";
          return (
            <div style={{ fontSize: "12px" }}>
              <div>{rows.toLocaleString()} rows</div>
              <div style={{ color: "#888" }}>{sizeMb} MB</div>
            </div>
          );
        },
      },
      {
        title: "Created",
        key: "createdAt",
        width: 130,
        render: (_, record) => {
          return (
            <span style={{ fontSize: "12px", color: "#666" }}>
              {moment(record.createdAt).format("DD MMM, HH:mm")}
            </span>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        width: 110,
        render: (_, record) => {
          if (record.status === "completed") {
            const isDownloading = downloadingJobId === record.id;
            return (
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                loading={isDownloading}
                onClick={() => this.handleDownload(record.id)}
              >
                Download
              </Button>
            );
          }
          if (record.status === "failed") {
            return (
              <Tooltip title={record.error_message || "Export encountered an error"}>
                <Tag color="error" style={{ cursor: "help" }}>
                  Error info
                </Tag>
              </Tooltip>
            );
          }
          if (record.status === "expired") {
            return <span style={{ color: "#999", fontSize: "12px" }}>Purged</span>;
          }
          return (
            <Button size="small" disabled loading>
              Wait
            </Button>
          );
        },
      },
    ];

    return (
      <Drawer
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Export Downloads</span>
            <Button
              size="small"
              icon={<SyncOutlined spin={isLoading} />}
              onClick={() => this.loadJobs(true)}
              style={{ marginRight: 24 }}
            >
              Refresh
            </Button>
          </div>
        }
        placement="right"
        width={680}
        onClose={onClose}
        visible={visible}
        bodyStyle={{ padding: "16px 12px" }}
      >
        <Table
          dataSource={jobs}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 8, size: "small" }}
          size="middle"
        />
      </Drawer>
    );
  }
}

export default ExportJobsDrawer;
